import './ui.css';

export type AvatarSize = 'sm' | 'md';

interface AvatarProps {
  /** Image URL. When empty/null the initials fallback is shown. */
  src?: string | null;
  /** Name used for initials and alt text. */
  name: string;
  size?: AvatarSize;
  /**
   * When true (default), the avatar is treated as decorative and hidden from
   * screen readers — the user's name is already rendered as adjacent text in
   * every table row and card. Set to false only when the avatar is the sole
   * means of identifying the user (e.g. a standalone profile image).
   */
  decorative?: boolean;
}

/** Extracts up to two uppercase letters from a display name. */
const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

/**
 * Circular avatar with an image or initials fallback.
 * `sm` (32px) is for table rows; `md` (40px) is for detail panels.
 */
export function Avatar({ src, name, size = 'sm', decorative = true }: AvatarProps) {
  const cls = `ui-avatar ui-avatar--${size}`;

  if (src) {
    return (
      <span className={cls} aria-hidden={decorative || undefined}>
        <img
          src={src}
          alt={decorative ? '' : name}
          className="ui-avatar__img"
          loading="lazy"
        />
      </span>
    );
  }

  return (
    <span className={cls} aria-hidden="true">
      <span className="ui-avatar__initials">{initials(name)}</span>
    </span>
  );
}
